import React from 'react';
import Header from './Header';
import Footer from './Footer';
import { ChevronRight, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getFileUrl } from '../utils/assetUrlHelper';

/**
 * Reusable layout component for all internal pages
 * Provides consistent header, footer, breadcrumbs, and page structure
 */
export default function InternalPageLayout({
  title,
  subtitle,
  breadcrumbs = [],
  children,
  heroImage = null,
  bannerImage = null, // Support both prop names
  heroOverlay = true
}) {
  // Use bannerImage if provided, otherwise fall back to heroImage
  // Process the image path using getFileUrl to handle various formats (e.g., data/files/)
  const rawImage = bannerImage || heroImage;
  const backgroundImage = rawImage ? getFileUrl(rawImage) : null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* Hero Section with optional background image */}
      <div className={`relative ${backgroundImage ? 'h-48 md:h-64' : 'bg-gradient-to-r from-[#0E6D2B] to-[#21A366] py-8 md:py-12'}`}>
        {backgroundImage && (
          <>
            <img
              src={backgroundImage}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                console.error('[InternalPageLayout] Banner load failed:', backgroundImage);
                e.target.style.display = 'none'; // Hide broken image
              }}
            />
            {heroOverlay && (
              <div className="absolute inset-0 bg-black/50" />
            )}
          </>
        )}
        <div className="container-cish relative z-10 h-full flex flex-col justify-center px-4 sm:px-6">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-3 md:mb-4">
            <ol className="flex items-center flex-wrap text-xs md:text-sm text-white/90">
              <li className="flex items-center">
                <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                  <Home className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Home</span>
                </Link>
              </li>
              {breadcrumbs.map((crumb, index) => (
                <li key={index} className="flex items-center">
                  <ChevronRight className="w-3 h-3 md:w-4 md:h-4 mx-1.5 flex-shrink-0" />
                  {crumb.href ? (
                    <Link to={crumb.href} className="hover:text-white transition-colors">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-white font-semibold">{crumb.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          {/* Page Title */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white break-words leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-2 text-sm md:text-base text-white/90 max-w-3xl break-words leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 py-6 md:py-10">
        <div className="container-cish">
          {children}
        </div>
      </main>

      <Footer />
    </div>
  );
}
